import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PasswordStrength, { evaluatePassword } from "@/components/PasswordStrength";
import { X, Lock, Trash2, UserPlus, LogIn, ArrowRight } from "lucide-react";

/**
 * Kick off Stripe checkout for the current cart. Split out so we can call it
 * both from the primary button and from the account-nudge modal after a
 * guest chooses to log in / register.
 */
async function startCheckout({ items, email, couponInfo, coupon, referral, marketingOptIn }) {
  const r = await api.post("/checkout", {
    items: items.map((i) => ({ ebook_id: i.ebook_id, quantity: i.quantity, format: i.format || "ebook" })),
    origin_url: window.location.origin,
    email,
    coupon_code: couponInfo ? coupon.toUpperCase() : null,
    referral_code: referral || null,
    marketing_opt_in: !!marketingOptIn,
  });
  window.location.href = r.data.checkout_url;
}

/**
 * Soft account nudge shown ONLY to guests who tap Secure Checkout. Presents
 * three equal-weight paths: create account, sign in, or continue as guest.
 * Never blocks a purchase.
 */
function AccountNudgeModal({ open, onClose, onProceed, checkoutArgs }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("choose"); // choose | register | login
  const [email, setEmail] = useState(checkoutArgs.email || "");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  // Reset internal step + password when the modal is dismissed so a re-open
  // starts fresh (previously it would resume mid-form).
  const handleClose = () => {
    setMode("choose");
    setPassword("");
    setBusy(false);
    onClose();
  };

  const proceedWithAuth = async (fn) => {
    setBusy(true);
    try {
      await fn();
      toast.success("Cart saved to your account.");
      // Auto-proceed to Stripe on the same click — cart is now merged, we
      // fire the /checkout call immediately.
      await startCheckout({ ...checkoutArgs, email });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    if (!evaluatePassword(password).rulesPassed) {
      toast.error("Password does not meet strength requirements"); return;
    }
    await proceedWithAuth(() => register(email, password, name));
  };
  const doLogin = async (e) => {
    e.preventDefault();
    await proceedWithAuth(() => login(email, password));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(10,37,64,0.62)", backdropFilter: "blur(6px)" }}
         data-testid="account-nudge-modal">
      <div className="bg-[#F7F5F0] max-w-lg w-full rounded-sm shadow-2xl relative"
           style={{ border: "1px solid rgba(10,37,64,0.10)" }}>
        <button onClick={handleClose} data-testid="nudge-close-btn"
                className="absolute top-4 right-4 text-navy/50 hover:text-navy transition-colors">
          <X className="w-4 h-4" />
        </button>

        {mode === "choose" && (
          <div className="p-10">
            <div className="eyebrow mb-3">One quick step</div>
            <h2 className="font-serif text-3xl text-navy leading-tight mb-2">Save your library to an account?</h2>
            <p className="text-sm text-navy/60 mt-3 leading-relaxed">
              With a free account you&apos;ll resume reading from any device, access your library
              anytime, and view your purchase history. Not now? No problem — you can still
              check out as a guest.
            </p>

            <div className="mt-8 space-y-3">
              <button onClick={() => setMode("register")} data-testid="nudge-register-btn"
                      className="w-full flex items-center justify-between px-5 py-4 border border-navy hover:bg-navy hover:text-white transition-colors duration-300 rounded-sm">
                <span className="flex items-center gap-3">
                  <UserPlus className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-widest">Create Free Account</span>
                </span>
                <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => setMode("login")} data-testid="nudge-login-btn"
                      className="w-full flex items-center justify-between px-5 py-4 border rounded-sm hover:border-gold transition-colors"
                      style={{ borderColor: "rgba(10,37,64,0.15)" }}>
                <span className="flex items-center gap-3">
                  <LogIn className="w-4 h-4 text-navy/70" />
                  <span className="text-xs uppercase tracking-widest text-navy/80">Sign In (Existing Member)</span>
                </span>
                <ArrowRight className="w-4 h-4 text-navy/60" />
              </button>
              <button onClick={() => onProceed()} data-testid="nudge-guest-btn"
                      disabled={busy}
                      className="w-full text-center text-sm text-navy/70 hover:text-gold underline underline-offset-4 pt-3">
                Continue as guest →
              </button>
            </div>
          </div>
        )}

        {mode === "register" && (
          <form onSubmit={doRegister} className="p-10 space-y-4">
            <button type="button" onClick={() => setMode("choose")} className="eyebrow text-navy/50 hover:text-navy">← Back</button>
            <h2 className="font-serif text-2xl text-navy mt-2">Create your account.</h2>
            <div>
              <label className="eyebrow block mb-1">Name</label>
              <input value={name} onChange={(e)=>setName(e.target.value)} data-testid="nudge-register-name"
                     className="w-full px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold"
                     style={{ borderColor: "rgba(10,37,64,0.15)" }} />
            </div>
            <div>
              <label className="eyebrow block mb-1">Email</label>
              <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="nudge-register-email"
                     className="w-full px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold"
                     style={{ borderColor: "rgba(10,37,64,0.15)" }} />
            </div>
            <div>
              <label className="eyebrow block mb-1">Password</label>
              <input type="password" required minLength={12} value={password} onChange={(e)=>setPassword(e.target.value)} data-testid="nudge-register-password"
                     className="w-full px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold"
                     style={{ borderColor: "rgba(10,37,64,0.15)" }} />
              <PasswordStrength password={password} />
            </div>
            <button type="submit" disabled={busy} data-testid="nudge-register-submit"
                    className="btn-primary w-full py-3 text-xs uppercase tracking-widest rounded-sm disabled:opacity-60">
              {busy ? "Setting up…" : "Create & Continue to Checkout"}
            </button>
          </form>
        )}

        {mode === "login" && (
          <form onSubmit={doLogin} className="p-10 space-y-4">
            <button type="button" onClick={() => setMode("choose")} className="eyebrow text-navy/50 hover:text-navy">← Back</button>
            <h2 className="font-serif text-2xl text-navy mt-2">Welcome back.</h2>
            <div>
              <label className="eyebrow block mb-1">Email</label>
              <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="nudge-login-email"
                     className="w-full px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold"
                     style={{ borderColor: "rgba(10,37,64,0.15)" }} />
            </div>
            <div>
              <label className="eyebrow block mb-1">Password</label>
              <input type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} data-testid="nudge-login-password"
                     className="w-full px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold"
                     style={{ borderColor: "rgba(10,37,64,0.15)" }} />
            </div>
            <button type="submit" disabled={busy} data-testid="nudge-login-submit"
                    className="btn-primary w-full py-3 text-xs uppercase tracking-widest rounded-sm disabled:opacity-60">
              {busy ? "Signing in…" : "Sign in & Continue to Checkout"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Cart() {
  const { items, remove, clear, subtotal, referral } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(user?.email || "");
  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null);
  const [marketingOptIn, setMarketingOptIn] = useState(true); // default: checked
  const [busy, setBusy] = useState(false);
  const [showNudge, setShowNudge] = useState(false);

  const validateCoupon = async () => {
    if (!coupon) return;
    try {
      const r = await api.get(`/coupons/${coupon.toUpperCase()}/validate`);
      setCouponInfo(r.data);
      toast.success(`Coupon applied: ${r.data.percent_off}% off`);
    } catch { setCouponInfo(null); toast.error("Invalid coupon"); }
  };

  const discount = couponInfo ? subtotal * (couponInfo.percent_off / 100) : 0;
  const total = subtotal - discount;

  const checkoutArgs = {
    items, email, couponInfo, coupon, referral: referral(),
    marketingOptIn,
  };

  const proceedToStripe = async () => {
    if (items.length === 0) return;
    if (!email) { toast.error("Enter your email for the receipt."); return; }
    setBusy(true);
    try {
      await startCheckout(checkoutArgs);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  const onCheckoutClick = () => {
    if (items.length === 0) return;
    if (!email) { toast.error("Enter your email for the receipt."); return; }
    // Signed-in users skip the nudge entirely.
    if (user) return proceedToStripe();
    setShowNudge(true);
  };

  const onGuestContinue = () => {
    setShowNudge(false);
    proceedToStripe();
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16" data-testid="cart-page">
      <AccountNudgeModal open={showNudge} onClose={() => setShowNudge(false)}
                          onProceed={onGuestContinue} checkoutArgs={checkoutArgs} />

      <div className="eyebrow mb-3">Checkout</div>
      <h1 className="aperture-h1 text-5xl mb-14">Your Cart.</h1>

      {items.length === 0 ? (
        <div className="text-center py-24" data-testid="cart-empty">
          <p className="text-navy/60 mb-6">Your cart is empty.</p>
          <Link to="/catalog" data-testid="cart-browse-btn" className="btn-primary px-8 py-3 text-xs uppercase tracking-widest rounded-sm">Browse Catalog</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-16">
          <div className="lg:col-span-2 space-y-6">
            {items.map((i) => (
              <div key={`${i.ebook_id}-${i.format || "ebook"}`} data-testid={`cart-item-${i.ebook_id}-${i.format || "ebook"}`} className="flex items-center gap-6 border-b border-navy-10 pb-6" style={{ borderColor: "rgba(10,37,64,0.10)" }}>
                <div className="w-20 h-28 bg-[#F7F5F0] rounded-sm shadow flex items-center justify-center p-1.5 flex-shrink-0">
                  <img src={i.cover} alt={i.title} className="max-w-full max-h-full w-auto h-auto object-contain" />
                </div>
                <div className="flex-1">
                  <div className="font-serif text-xl text-navy">{i.title}</div>
                  <div className="mt-0.5 text-[10px] uppercase tracking-widest text-navy/50">{i.format_label || "eBook (PDF)"}</div>
                  <div className="mt-1 text-gold">${i.price.toFixed(2)}</div>
                </div>
                <button onClick={() => remove(i.ebook_id, i.format || "ebook")} data-testid={`cart-remove-${i.ebook_id}-${i.format || "ebook"}`} className="text-navy/40 hover:text-navy transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}

            <div className="pt-2">
              <button onClick={() => {
                if (!window.confirm("Remove everything from your cart?")) return;
                clear(); toast.success("Cart cleared");
              }}
                data-testid="cart-clear-btn"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> Clear cart
              </button>
            </div>
          </div>

          <aside className="bg-white p-8 shadow-xl h-fit rounded-sm">
            <div className="eyebrow mb-4">Summary</div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-navy/70">Subtotal</span><span className="text-navy" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span></div>
              {couponInfo && <div className="flex justify-between text-gold"><span>Discount ({couponInfo.percent_off}%)</span><span>-${discount.toFixed(2)}</span></div>}
              <div className="border-t border-navy-10 pt-3 mt-3 flex justify-between text-lg font-serif" style={{ borderColor: "rgba(10,37,64,0.10)" }}><span>Total</span><span data-testid="cart-total">${total.toFixed(2)}</span></div>
            </div>

            <div className="mt-6 space-y-3">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for receipt & download"
                data-testid="cart-email" className="w-full px-4 py-3 border border-navy-10 focus:outline-none focus:border-gold text-sm rounded-sm" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
              <div className="flex gap-2">
                <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon code"
                  data-testid="cart-coupon" className="flex-1 px-4 py-3 border border-navy-10 focus:outline-none focus:border-gold text-sm rounded-sm" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
                <button onClick={validateCoupon} data-testid="cart-apply-coupon" className="px-4 py-3 border border-navy text-navy text-xs uppercase tracking-widest hover:bg-navy hover:text-white transition-colors duration-300 rounded-sm">Apply</button>
              </div>
              {referral() && <div className="text-xs text-navy/60">Referral: <span className="text-gold">{referral()}</span></div>}

              <label className="flex items-start gap-3 pt-2 cursor-pointer select-none"
                     data-testid="cart-marketing-optin-label">
                <input type="checkbox" checked={marketingOptIn}
                       onChange={(e) => setMarketingOptIn(e.target.checked)}
                       data-testid="cart-marketing-optin"
                       className="mt-[3px] w-4 h-4 accent-gold cursor-pointer" />
                <span className="text-xs text-navy/70 leading-relaxed">
                  Keep me updated on new releases, member deals, and exclusive
                  chapters from Aperture Enterprises. No spam — unsubscribe anytime.
                </span>
              </label>
            </div>

            <button onClick={onCheckoutClick} disabled={busy} data-testid="cart-checkout-btn"
              className="btn-primary w-full mt-6 py-4 text-xs uppercase tracking-[0.24em] flex items-center justify-center gap-2 rounded-sm disabled:opacity-60">
              <Lock className="w-3.5 h-3.5" strokeWidth={2} /> {busy ? "Redirecting…" : "Secure Checkout"}
            </button>
            <div className="mt-4 text-[10px] text-navy/50 uppercase tracking-widest text-center">Stripe · SSL · 100% guaranteed</div>
            <div className="mt-3 text-[10px] text-navy/40 text-center">PayPal — coming soon</div>
          </aside>
        </div>
      )}
    </div>
  );
}
